/**
 * qty-discount.js
 *
 * Quantity-based price discount widget for the `product.qty-discount` template.
 *
 * WHY this script is loaded from the top of main-product.liquid (not the snippet):
 *   The price block carries class `variant-changed-update`, so the theme replaces it
 *   via outerHTML on every variant switch. Scripts injected via outerHTML don't re-execute,
 *   and any stored DOM references become stale. Loading from the section level (outside the
 *   swappable zone) keeps one persistent script that re-queries the DOM on each update.
 *
 * TIERS — read from product metafield `custom.qty_tiers` (JSON).
 *   Set per-product in Admin → Products → [product] → Metafields.
 *   Format: [{ "minQty": 2, "maxQty": 4, "pct": 5 }, ...]
 *   The full-price row (qty 1) is always prepended automatically.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Tier definition — read from the widget's data-tiers attribute (metafield)
  // The full-price row (minQty: 1, pct: 0) is prepended automatically.
  // ---------------------------------------------------------------------------
  function getTiers() {
    const widget = getWidget();
    if (!widget) return [];
    try {
      const metafieldTiers = JSON.parse(widget.dataset.tiers || '[]');
      return [{ minQty: 1, maxQty: 1, pct: 0 }, ...metafieldTiers];
    } catch (e) {
      return [{ minQty: 1, maxQty: 1, pct: 0 }];
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Format cents → money string (uses Shopify global if available). */
  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(
        cents,
        window.Shopify.money_format || '${{amount}}'
      );
    }
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /** Return the matching tier for a given quantity. */
  function getTierForQty(qty) {
    const tiers = getTiers();
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (qty >= tiers[i].minQty) return tiers[i];
    }
    return tiers[0];
  }

  // ---------------------------------------------------------------------------
  // DOM queries — always called fresh (widget can be replaced by variant swap)
  // ---------------------------------------------------------------------------

  function getWidget()     { return document.getElementById('qty-discount-widget'); }
  function getSectionId()  { const w = getWidget(); return w ? w.dataset.sectionId : null; }
  function getQtyInput()   { const id = getSectionId(); return id ? document.getElementById('quantity-input-' + id) : null; }
  function getPriceBlock() { const id = getSectionId(); return id ? document.getElementById('price-' + id) : null; }

  /** Read the current base price in cents from the widget's data attribute. */
  function getBasePriceCents() {
    const w = getWidget();
    return w ? (parseInt(w.dataset.basePrice, 10) || 0) : 0;
  }

  // ---------------------------------------------------------------------------
  // Price DOM manipulation
  // ---------------------------------------------------------------------------

  function applyPriceDiscount(discountPct) {
    const priceBlock   = getPriceBlock();
    const basePriceCents = getBasePriceCents();
    if (!priceBlock || !basePriceCents) return;

    const priceEl     = priceBlock.querySelector('.price');
    const regularItem = priceBlock.querySelector('.price-regular .price-item-regular');
    if (!priceEl || !regularItem) return;

    if (discountPct === 0) {
      // Restore full price
      priceEl.classList.remove('price-on-sale');
      regularItem.textContent = formatMoney(basePriceCents);
      const injected = priceBlock.querySelector('.qty-discount-sale-block');
      if (injected) injected.remove();
      return;
    }

    const discountedCents = Math.round(basePriceCents * (1 - discountPct / 100));

    // Inject (or update) the sale block
    let saleBlock = priceBlock.querySelector('.qty-discount-sale-block');
    if (!saleBlock) {
      saleBlock = document.createElement('div');
      saleBlock.className = 'price-sale qty-discount-sale-block';
      saleBlock.innerHTML =
        '<span>' +
          '<span class="visually-hidden">Regular price</span>' +
          '<s class="price-item price-item-regular qty-discount-compare"></s>' +
        '</span>' +
        '<span class="visually-hidden">Sale price</span>' +
        '<span class="price-item price-item-sale price-item-last qty-discount-sale-price"></span>';

      const container = priceBlock.querySelector('.price-container');
      if (container) container.appendChild(saleBlock);
    }

    priceEl.classList.add('price-on-sale');
    regularItem.textContent = formatMoney(discountedCents);
    saleBlock.querySelector('.qty-discount-compare').textContent    = formatMoney(basePriceCents);
    saleBlock.querySelector('.qty-discount-sale-price').textContent = formatMoney(discountedCents);
  }

  /** Highlight the active tier row and update the per-unit price column. */
  function updateTable(activeTier) {
    const widget = getWidget();
    if (!widget) return;

    const basePriceCents = getBasePriceCents();

    widget.querySelectorAll('.qty-discount-row').forEach((row) => {
      const min    = parseInt(row.dataset.minQty, 10);
      const max    = parseInt(row.dataset.maxQty, 10);
      const pct    = parseInt(row.dataset.pct, 10);
      const active = activeTier.minQty === min && activeTier.maxQty === max;

      row.classList.toggle('qty-discount-row--active', active);

      // Keep per-unit price column in sync with the current variant
      const priceCell = row.querySelector('.qty-discount-price');
      if (priceCell && basePriceCents) {
        priceCell.textContent = formatMoney(Math.round(basePriceCents * (1 - pct / 100)));
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Core update — re-queries everything fresh each call
  // ---------------------------------------------------------------------------

  function update() {
    const qtyInput = getQtyInput();
    const qty      = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    const tier     = getTierForQty(qty);
    applyPriceDiscount(tier.pct);
    updateTable(tier);
  }

  // ---------------------------------------------------------------------------
  // Event wiring
  // ---------------------------------------------------------------------------

  function wireQtyInput() {
    const qtyInput = getQtyInput();
    if (!qtyInput || qtyInput._qtyDiscountWired) return;
    qtyInput._qtyDiscountWired = true; // prevent double-wiring

    qtyInput.addEventListener('change', update);
    qtyInput.addEventListener('input',  update);
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  function init() {
    if (!getWidget()) return; // not on a qty-discount product page

    wireQtyInput();
    update();

    // Re-apply after every variant swap (widget gets fresh data-base-price from server)
    document.addEventListener('product:variant-change', function () {
      // The variant-changed-update block is replaced asynchronously;
      // wait one tick for the new DOM to settle before re-querying.
      requestAnimationFrame(function () {
        wireQtyInput(); // qty input may have been re-created too
        update();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
